import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiArrowLeft, FiArrowRight, FiBookOpen, FiCheck, FiChevronDown, FiClock,
  FiEdit3, FiHeadphones, FiMic, FiPlay, FiPlus, FiRefreshCw, FiRotateCcw,
  FiSquare, FiVolume2, FiZap,
} from 'react-icons/fi';

import { useI18n } from '../i18n/I18nProvider';
import DictationQuestion from '../components/DictationQuestion';
import PronunciationBoard from '../components/PronunciationBoard';
import { playTTS, skipAllTTS } from './WordPracticePage';
import { beginAudioSession, disposeAudioSession, prefetchSessionAudio } from '../lib/sessionAudioCache';
import {
  collectPronunciationWords, pronunciationTokenKey, tokenizePronunciationSentence,
} from '../lib/pronunciationTokens';
import {
  confirmPronunciationCard, initializePronunciationBoard, pronunciationItemKey,
  updatePronunciationCard,
} from '../lib/pronunciationBoard';
import {
  buildIeltsSpeakingLaunchUrl,
  fetchIeltsSpeakingHistory,
} from '../lib/ieltsSpeaking';
import {
  buildGroupMeaningChoices, buildMeaningChoices, diffCharacters, formatDuration, groupIdentity,
  groupSelfCheckItems, MEANING_SPRINT_BASE_MS, meaningTimeLimit, mergeWords, normalizeAnswer,
  parseWordText, shuffle, suddenTimeLimit, updateMeaningSelection,
} from '../lib/selfCheck';

const authHeaders = (json = false) => ({
  ...(json ? { 'Content-Type': 'application/json' } : {}),
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const EXPERIMENTS = [
  {
    id: 'adaptive-reading',
    route: '/student/self-check/adaptive-reading',
    title: 'Adaptive IELTS-style Reading',
    description: 'Turn recent vocabulary and sentence difficulties into a fresh 20-minute reading.',
    duration: '20 min',
  },
  {
    id: 'ielts-speaking',
    route: '/student/self-check/ielts-speaking',
    title: 'IELTS Speaking Practice',
    description: 'Record an answer, see a predicted band, and keep a coaching thread for repeat practice.',
    duration: '1 answer',
  },
];

const Card = ({ children, className = '' }) => (
  <section className={`border border-gray-200 bg-white rounded-lg shadow-sm ${className}`}>{children}</section>
);

const fetchWordAudio = async (word) => {
  const response = await fetch('/api/tts/say', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ text: word, mode: 'word' }),
  });
  if (!response.ok) throw new Error(`Could not prepare audio for ${word}`);
  return URL.createObjectURL(await response.blob());
};

const prefetchWordAudio = async (words, onProgress, concurrency = 6) => {
  const uniqueWords = [...new Set(words.map((word) => word.trim()).filter(Boolean))];
  const urls = new Map();
  let cursor = 0;
  let completed = 0;
  const worker = async () => {
    while (cursor < uniqueWords.length) {
      const word = uniqueWords[cursor];
      cursor += 1;
      try {
        urls.set(word.toLocaleLowerCase(), await fetchWordAudio(word));
      } finally {
        completed += 1;
        onProgress?.(completed, uniqueWords.length);
      }
    }
  };
  await Promise.all(Array.from(
    { length: Math.min(concurrency, uniqueWords.length) },
    () => worker(),
  ));
  return urls;
};

const playPreparedAudio = (audioRef, url) => {
  if (!url) return;
  if (!audioRef.current) audioRef.current = new Audio();
  audioRef.current.pause();
  audioRef.current.src = url;
  audioRef.current.currentTime = 0;
  audioRef.current.play().catch(() => {});
};

const playRecording = (audioRef, url) => {
  if (!url) return;
  skipAllTTS();
  playPreparedAudio(audioRef, url);
};

function useDeadline(limitMs, questionKey, active, onExpire) {
  const [remaining, setRemaining] = useState(limitMs);
  const callbackRef = useRef(onExpire);
  const deadlineRef = useRef(0);
  const pausedRemainingRef = useRef(limitMs);
  callbackRef.current = onExpire;

  useEffect(() => {
    if (!active) return undefined;
    let frame;
    let expired = false;
    deadlineRef.current = performance.now() + limitMs;
    pausedRemainingRef.current = limitMs;
    setRemaining(limitMs);

    const tick = () => {
      if (document.hidden) {
        frame = requestAnimationFrame(tick);
        return;
      }
      const next = Math.max(0, deadlineRef.current - performance.now());
      pausedRemainingRef.current = next;
      setRemaining(next);
      if (next <= 0 && !expired) {
        expired = true;
        callbackRef.current();
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    const visibility = () => {
      if (document.hidden) {
        pausedRemainingRef.current = Math.max(0, deadlineRef.current - performance.now());
      } else {
        deadlineRef.current = performance.now() + pausedRemainingRef.current;
      }
    };
    document.addEventListener('visibilitychange', visibility);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [limitMs, questionKey, active]);
  return remaining;
}

const recentSelfCheckLabel = (session) => {
  if (session.mode === 'dictation') return 'Dictation Loop';
  if (session.mode === 'meaning_sprint') return '7-Second Meaning';
  if (session.mode === 'pronunciation') return 'Pronunciation Practice';
  if (session.mode === 'ielts_speaking') return 'IELTS Speaking Practice';
  return 'Lightning Challenge';
};

const WordSetBuilder = ({ sources, selectedDate, setSelectedDate, text, setText, onPrepare, resolving, history, onOpenHistory }) => {
  const { t } = useI18n();
  const words = useMemo(() => parseWordText(text), [text]);
  const add = (values) => setText((current) => mergeWords(current, values));
  return (
    <div className="space-y-6">
      <Card className="p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600">{t('Build your set')}</div>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">{t('What do you want to check today?')}</h2>
            <p className="mt-2 text-sm text-gray-600">{t('Paste a list or bring back words from your own study history. You can edit everything before starting.')}</p>
          </div>
          <label className="text-sm text-gray-600">
            <span className="block mb-1 font-medium">{t('Study date')}</span>
            <select value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} className="border rounded-md px-3 py-2 bg-white">
              <option value={sources?.date || selectedDate}>{sources?.date || selectedDate}</option>
              {(sources?.recent_dates || []).filter((item) => item.date !== (sources?.date || selectedDate)).map((item) => (
                <option key={item.date} value={item.date}>{item.date} · {item.learned_count + item.reviewed_count}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-5">
          <button onClick={() => add(sources?.learned)} className="text-left border rounded-lg p-4 hover:border-indigo-400 hover:bg-indigo-50 disabled:opacity-50" disabled={!sources?.learned?.length}>
            <FiPlus className="text-indigo-600 mb-2" /><span className="font-semibold block">{t('Add learned words')}</span>
            <span className="text-sm text-gray-500">{sources?.learned?.length || 0} {t('available')}</span>
          </button>
          <button onClick={() => add(sources?.reviewed)} className="text-left border rounded-lg p-4 hover:border-indigo-400 hover:bg-indigo-50 disabled:opacity-50" disabled={!sources?.reviewed?.length}>
            <FiRefreshCw className="text-indigo-600 mb-2" /><span className="font-semibold block">{t('Add reviewed words')}</span>
            <span className="text-sm text-gray-500">{sources?.reviewed?.length || 0} {t('available')}</span>
          </button>
          <button onClick={() => add(sources?.due_review)} className="text-left border rounded-lg p-4 hover:border-indigo-400 hover:bg-indigo-50 disabled:opacity-50" disabled={!sources?.due_review?.length}>
            <FiClock className="text-indigo-600 mb-2" /><span className="font-semibold block">{t('Add due reviews')}</span>
            <span className="text-sm text-gray-500">{sources?.due_review?.length || 0} {t('available')}</span>
          </button>
        </div>

        <label className="block mt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-gray-800">{t('Your word list')}</span>
            <span className="text-sm text-gray-500">{words.length}/200</span>
          </div>
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={t('One word or phrase per line')}
            className="w-full min-h-[220px] border rounded-lg p-4 font-mono text-base focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            data-i18n-skip
          />
        </label>
        <div className="mt-4 flex justify-end">
          <button disabled={!words.length || resolving} onClick={onPrepare} className="inline-flex items-center gap-2 px-5 py-3 bg-indigo-600 text-white rounded-md font-semibold hover:bg-indigo-700 disabled:opacity-50">
            {resolving ? <FiRefreshCw className="animate-spin" /> : <FiPlay />} {t('Choose a test mode')}
          </button>
        </div>
      </Card>

      {history?.length > 0 && (
        <Card className="p-5">
          <h3 className="font-bold text-gray-900">{t('Recent self-checks')}</h3>
          <div className="mt-3 divide-y">
            {history.slice(0, 6).map((session) => {
              const isIelts = session.mode === 'ielts_speaking';
              const content = (
                <>
                  <div>
                    <span className="font-semibold">{t(recentSelfCheckLabel(session))}</span>
                    <span className="text-gray-500 ml-2">{new Date(session.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-gray-600">
                    {isIelts
                      ? session.predicted_overall == null
                        ? t('Recording saved')
                        : `${t('Predicted band')} ${session.predicted_overall}`
                      : `${session.word_count} ${t('words')} · ${Math.round((session.first_pass_accuracy || 0) * 100)}%`}
                    {isIelts && <span className="ml-2 font-semibold text-indigo-700">{t('View result and recording')}</span>}
                  </div>
                </>
              );
              return isIelts ? (
                <button
                  type="button"
                  key={`ielts-${session.id}`}
                  onClick={() => onOpenHistory(session)}
                  className="w-full py-3 flex flex-wrap items-center justify-between gap-2 text-left text-sm hover:bg-indigo-50"
                >
                  {content}
                </button>
              ) : (
                <div key={`self-check-${session.id}`} className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                  {content}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

const ModePicker = ({ items, unresolved, onBack, onStart }) => {
  const { t } = useI18n();
  const pronunciationCount = items.filter((item) => item.pronunciation_sentence).length;
  const modes = [
    { id: 'dictation', icon: FiHeadphones, title: 'Dictation Loop', detail: 'Hear the word, spell it, then choose its definition. Only missed words return in the next round.', meta: 'Spelling and meaning' },
    { id: 'meaning_sprint', icon: FiClock, title: '7-Second Meaning', detail: 'Match each word to its meaning within seven seconds. Missed words return in the next round.', meta: 'First-pass accuracy' },
    { id: 'sudden_sprint', icon: FiZap, title: 'Lightning Challenge', detail: 'The clock falls from five seconds toward two. One miss ends the run. How long can you hold on?', meta: 'Best streak' },
    { id: 'pronunciation', icon: FiMic, title: 'Pronunciation Practice', detail: 'Listen to a short sentence, read it aloud, and get focused feedback on whether the target word is clear.', meta: '{count} sentences ready', metaParams: { count: pronunciationCount }, disabled: pronunciationCount === 0 },
  ];
  return (
    <Card className="p-5 sm:p-7">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"><FiArrowLeft /> {t('Edit word list')}</button>
      <div className="mt-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600">{items.length} {t('ready')}</div>
        <h2 className="text-2xl font-bold mt-1">{t('Choose how to test yourself')}</h2>
        <p className="text-gray-600 mt-2">{t('These results are private practice records and do not change your formal learning progress.')}</p>
      </div>
      {unresolved.length > 0 && (
        <div className="mt-4 border border-amber-200 bg-amber-50 rounded-lg p-4 text-sm text-amber-900">
          <strong>{t('Not found and skipped:')}</strong> <span data-i18n-skip>{unresolved.join(', ')}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {modes.map(({ id, icon: Icon, title, detail, meta, metaParams, disabled }) => (
          <button key={id} disabled={disabled} onClick={() => onStart(id)} className="group text-left border rounded-lg p-5 hover:border-indigo-500 hover:shadow-md focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:shadow-none">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center"><Icon size={21} /></div>
            <h3 className="mt-4 text-lg font-bold">{t(title)}</h3>
            <p className="mt-2 text-sm text-gray-600 min-h-[64px]">{t(detail)}</p>
            <div className="mt-4 text-xs uppercase font-semibold text-indigo-600">{t(meta, metaParams)}</div>
          </button>
        ))}
      </div>
    </Card>
  );
};

const preferredRecordingType = () => {
  if (!window.MediaRecorder) return '';
  return [
    'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4',
  ].find((type) => window.MediaRecorder.isTypeSupported?.(type)) || '';
};

const PRONUNCIATION_WORD_TAP_KEY = 'lexilab.pronunciation.word-tap';

const SingleCardPronunciationRunner = ({ items, onFinish, onExit }) => {
  const { t, locale } = useI18n();
  const allItems = useMemo(() => items.filter((item) => item.pronunciation_sentence), [items]);
  const [queue, setQueue] = useState(() => allItems);
  const [index, setIndex] = useState(0);
  const [round, setRound] = useState(1);
  const [recording, setRecording] = useState(false);
  const [grading, setGrading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState('');
  const [error, setError] = useState('');
  const [audioProgress, setAudioProgress] = useState({ done: 0, total: Math.min(5, queue.length), failed: 0 });
  const [wordAudioProgress, setWordAudioProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [wordTapEnabled, setWordTapEnabled] = useState(() => {
    try {
      return localStorage.getItem(PRONUNCIATION_WORD_TAP_KEY) === '1';
    } catch (_) {
      return false;
    }
  });
  const attemptsByKeyRef = useRef(new Map());
  const attemptsRef = useRef([]);
  const passedRef = useRef(new Set());
  const mistakesRef = useRef(new Map());
  const roundMistakesRef = useRef(new Map());
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingUrlRef = useRef('');
  const recordingAudioRef = useRef(null);
  const startedRef = useRef(performance.now());
  const current = queue[index];
  const pronunciationWords = useMemo(() => collectPronunciationWords(allItems), [allItems]);
  const pronunciationWordMap = useMemo(() => new Map(
    pronunciationWords.map((word) => [pronunciationTokenKey(word), word]),
  ), [pronunciationWords]);

  const sentenceTokens = useMemo(
    () => tokenizePronunciationSentence(current?.pronunciation_sentence),
    [current?.pronunciation_sentence],
  );

  const stopStream = () => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    const sessionKey = allItems.map(pronunciationItemKey).join('|');
    beginAudioSession(`pronunciation:${sessionKey}`);
    const first = allItems.slice(0, 5).map((item) => ({ text: item.pronunciation_sentence, mode: 'sentence' }));
    prefetchSessionAudio(first, {
      concurrency: 5,
      onProgress: setAudioProgress,
    });
    prefetchSessionAudio(
      pronunciationWords.map((word) => ({ text: word, mode: 'word' })),
      { concurrency: 6, onProgress: setWordAudioProgress },
    );
    return () => {
      recorderRef.current?.state === 'recording' && recorderRef.current.stop();
      stopStream();
      skipAllTTS();
      disposeAudioSession();
      recordingAudioRef.current?.pause?.();
      if (recordingUrlRef.current) URL.revokeObjectURL(recordingUrlRef.current);
      recordingUrlRef.current = '';
    };
  }, [allItems, pronunciationWords]);

  const toggleWordTap = () => {
    const next = !wordTapEnabled;
    setWordTapEnabled(next);
    try {
      localStorage.setItem(PRONUNCIATION_WORD_TAP_KEY, next ? '1' : '0');
    } catch (_) {
      // Storage is optional; the control still works for this session.
    }
  };

  const playWordToken = (token) => {
    if (recording || grading || !token?.speakable) return;
    playTTS(pronunciationWordMap.get(token.key) || token.text, 'word');
  };

  useEffect(() => {
    setFeedback(null);
    setError('');
    if (!current) return;
    playTTS(current.pronunciation_sentence, 'sentence');
    const upcoming = queue.slice(index + 1, index + 6).map((item) => ({
      text: item.pronunciation_sentence,
      mode: 'sentence',
    }));
    prefetchSessionAudio(upcoming, { concurrency: 5 });
  }, [current, index, queue]);

  const finish = useCallback(() => {
    const passed = passedRef.current.size;
    const attempts = attemptsRef.current;
    onFinish({
      mode: 'pronunciation',
      words: allItems.map(resultEntry),
      duration_ms: performance.now() - startedRef.current,
      first_pass_accuracy: allItems.length ? attempts.filter((attempt) => attempt.attempt_number === 1 && attempt.passed).length / allItems.length : 0,
      completion_rate: allItems.length ? passed / allItems.length : 0,
      rounds: round,
      average_response_ms: attempts.length ? attempts.reduce((sum, attempt) => sum + attempt.response_ms, 0) / attempts.length : 0,
      mistakes: [...mistakesRef.current.values()].map(resultEntry),
      attempts,
      max_streak: passed,
    });
  }, [allItems, onFinish, round]);

  const advance = () => {
    setFeedback(null);
    if (index + 1 < queue.length) {
      setIndex((value) => value + 1);
      return;
    }
    const nextRound = [...roundMistakesRef.current.values()];
    if (nextRound.length) {
      roundMistakesRef.current = new Map();
      setQueue(shuffle(nextRound));
      setRound((value) => value + 1);
      setIndex(0);
      return;
    }
    finish();
  };

  const gradeBlob = async (blob, elapsedMs) => {
    if (!current) return;
    setGrading(true);
    setError('');
    const key = pronunciationItemKey(current);
    const attemptNumber = (attemptsByKeyRef.current.get(key) || 0) + 1;
    attemptsByKeyRef.current.set(key, attemptNumber);
    try {
      const body = new FormData();
      body.append('audio', blob, `reading.${blob.type.includes('mp4') ? 'm4a' : 'webm'}`);
      body.append('word', current.word);
      body.append('sense_id', current.sense_id || 'primary');
      body.append('locale', locale);
      const response = await fetch('/api/student/self-check/pronunciation/grade', {
        method: 'POST',
        headers: authHeaders(false),
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Your recording could not be scored right now');
      const attempt = {
        word: current.word,
        sense_ids: [current.sense_id || 'primary'],
        correct: Boolean(data.passed),
        passed: Boolean(data.passed),
        score: data.score,
        spelling_correct: true,
        meaning_correct: true,
        timed_out: false,
        round,
        attempt_number: attemptNumber,
        response_ms: elapsedMs,
      };
      attemptsRef.current.push(attempt);
      if (data.passed) {
        passedRef.current.add(key);
        mistakesRef.current.delete(key);
      } else {
        mistakesRef.current.set(key, current);
        roundMistakesRef.current.set(key, current);
      }
      setFeedback({ ...data, attemptNumber });
    } catch (gradeError) {
      attemptsByKeyRef.current.set(key, Math.max(0, attemptNumber - 1));
      setError(gradeError.message);
    } finally {
      setGrading(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError('Recording is not supported in this browser.');
      return;
    }
    try {
      skipAllTTS();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = preferredRecordingType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];
      const startedAt = performance.now();
      recorder.ondataavailable = (event) => event.data?.size && chunksRef.current.push(event.data);
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        stopStream();
        if (recordingUrlRef.current) URL.revokeObjectURL(recordingUrlRef.current);
        recordingUrlRef.current = URL.createObjectURL(blob);
        setRecordingUrl(recordingUrlRef.current);
        gradeBlob(blob, performance.now() - startedAt);
      };
      recorder.start();
      setRecording(true);
      setFeedback(null);
      setError('');
    } catch (_) {
      stopStream();
      setError('Microphone access is needed for pronunciation practice.');
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    setRecording(false);
  };

  if (!current) {
    return <Card className="max-w-3xl mx-auto p-7"><p>{t('No pronunciation sentences are ready for this set yet.')}</p><button onClick={onExit} className="mt-4 border px-4 py-2 rounded-md">{t('Back')}</button></Card>;
  }
  return (
    <Card className="max-w-4xl mx-auto overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-900"><FiArrowLeft /></button>
        <div className="text-sm text-gray-600">{t('Round')} {round} · {index + 1}/{queue.length}</div>
        <button onClick={() => playTTS(current.pronunciation_sentence, 'sentence')} className="p-2 border rounded-md text-indigo-700" title={t('Replay sentence')}><FiVolume2 /></button>
      </div>
      {audioProgress.total > 0 && audioProgress.done < audioProgress.total && (
        <div className="h-1 bg-indigo-50"><div className="h-full bg-indigo-500 transition-[width]" style={{ width: `${(audioProgress.done / audioProgress.total) * 100}%` }} /></div>
      )}
      <div className="p-6 sm:p-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs uppercase font-semibold tracking-wide text-indigo-600">{t('Target word')}</div>
          <button
            type="button"
            onClick={toggleWordTap}
            aria-pressed={wordTapEnabled}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${wordTapEnabled ? 'border-indigo-300 bg-indigo-50 text-indigo-800' : 'border-gray-200 bg-white text-gray-700'}`}
          >
            <FiVolume2 /> {t('Tap words to listen')}
          </button>
        </div>
        <h2 className="mt-1 text-3xl font-bold" data-i18n-skip>{current.display_label || current.word}</h2>
        {wordAudioProgress.total > 0 && wordAudioProgress.done < wordAudioProgress.total && (
          <div className="mt-4 max-w-sm" aria-live="polite">
            <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
              <span>{t('Preparing word audio')}</span>
              <span className="tabular-nums">{wordAudioProgress.done}/{wordAudioProgress.total}</span>
            </div>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full bg-indigo-500 transition-[width]" style={{ width: `${(wordAudioProgress.done / wordAudioProgress.total) * 100}%` }} />
            </div>
          </div>
        )}
        <p className="mt-6 text-xl leading-9 text-gray-900" data-i18n-skip>
          {wordTapEnabled ? sentenceTokens.map((token, tokenIndex) => (
            token.speakable ? (
              <button
                type="button"
                key={`${token.key}-${tokenIndex}`}
                onClick={() => playWordToken(token)}
                disabled={recording || grading}
                className="rounded-sm px-0.5 text-left text-indigo-800 underline decoration-indigo-200 decoration-2 underline-offset-4 hover:bg-indigo-50 hover:decoration-indigo-500 disabled:cursor-default disabled:opacity-60"
                title={t('Play this word')}
              >
                {token.text}
              </button>
            ) : <React.Fragment key={`text-${tokenIndex}`}>{token.text}</React.Fragment>
          )) : current.pronunciation_sentence}
        </p>
        {wordTapEnabled && <p className="mt-2 text-sm text-indigo-700">{t('Tap any word in the sentence to hear it.')}</p>}
        <p className="mt-2 text-sm text-gray-500">{t('Listen once, then read the sentence aloud. A clear target word matters most.')}</p>
        <div className="mt-7 flex flex-wrap gap-3">
          <button onClick={() => playTTS(current.pronunciation_sentence, 'sentence')} disabled={recording || grading} className="inline-flex items-center gap-2 border px-5 py-3 rounded-md font-semibold disabled:opacity-50"><FiVolume2 /> {t('Listen again')}</button>
          {!recording ? (
            <button onClick={startRecording} disabled={grading} className="inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-3 rounded-md font-semibold disabled:opacity-50"><FiMic /> {grading ? t('Scoring your reading...') : t('Start recording')}</button>
          ) : (
            <button onClick={stopRecording} className="inline-flex items-center gap-2 bg-rose-600 text-white px-5 py-3 rounded-md font-semibold"><FiSquare /> {t('Finish recording')}</button>
          )}
        </div>
        {error && <div className="mt-5 border border-rose-200 bg-rose-50 text-rose-800 rounded-md p-4">{t(error)}</div>}
        {feedback && (
          <div className={`mt-7 border rounded-lg p-5 ${feedback.passed ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
            <div className="flex items-center justify-between gap-4"><h3 className="font-bold text-lg">{feedback.passed ? t('Passed') : t('Try once more')}</h3><div className="text-3xl font-bold tabular-nums">{feedback.score}/100</div></div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><span className="text-gray-500">{t('Clarity')}</span><strong className="block">{feedback.intelligibility}/50</strong></div>
              <div><span className="text-gray-500">{t('Target word')}</span><strong className="block" data-i18n-skip>{feedback.target_word}/25</strong></div>
              <div><span className="text-gray-500">{t('Completeness')}</span><strong className="block">{feedback.completeness}/15</strong></div>
              <div><span className="text-gray-500">{t('Rhythm')}</span><strong className="block">{feedback.rhythm}/10</strong></div>
            </div>
            <p className="mt-4 text-gray-800" data-i18n-skip>{feedback.feedback}</p>
            {!feedback.passed && feedback.issues?.length > 0 && (
              <div className="mt-4 border-t border-amber-200 pt-4">
                <h4 className="font-semibold text-amber-950">{t('What to fix')}</h4>
                <div className="mt-3 space-y-3">
                  {feedback.issues.map((issue, issueIndex) => (
                    <div key={`${issue.segment}-${issueIndex}`} className="rounded-md bg-white/70 p-3 text-sm" data-i18n-skip>
                      <div className="font-semibold text-gray-950">{issue.segment}</div>
                      <div className="mt-1 text-gray-700">{issue.problem}</div>
                      <div className="mt-1 text-indigo-800">{issue.correction}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-2 text-sm text-gray-600" data-i18n-skip>{feedback.next_step}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              {recordingUrl && (
                <button type="button" onClick={() => playRecording(recordingAudioRef, recordingUrl)} className="inline-flex items-center gap-2 border border-gray-300 bg-white px-5 py-2.5 rounded-md font-semibold text-gray-800">
                  <FiPlay /> {t('Listen to my recording')}
                </button>
              )}
              {feedback.passed && <button onClick={advance} className="bg-emerald-700 text-white px-5 py-2.5 rounded-md font-semibold">{index + 1 === queue.length && roundMistakesRef.current.size === 0 ? t('See results') : t('Next sentence')}</button>}
              {!feedback.passed && (
                <div className="w-full">
                  <p className="mb-3 text-sm font-medium text-amber-900">{t('This word will return in the next round.')}</p>
                  <button onClick={advance} className="bg-gray-900 text-white px-5 py-2.5 rounded-md font-semibold">{t('Continue')}</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

const LegacyPronunciationRunner = ({ items, onFinish, onExit }) => {
  const { t, locale } = useI18n();
  const allItems = useMemo(() => items.filter((item) => item.pronunciation_sentence), [items]);
  const [board, setBoard] = useState(() => initializePronunciationBoard(allItems));
  const [recordingKey, setRecordingKey] = useState('');
  const [passedCount, setPassedCount] = useState(0);
  const [audioProgress, setAudioProgress] = useState({ done: 0, total: Math.min(6, allItems.length), failed: 0 });
  const [wordAudioProgress, setWordAudioProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [wordTapEnabled, setWordTapEnabled] = useState(() => {
    try {
      return localStorage.getItem(PRONUNCIATION_WORD_TAP_KEY) === '1';
    } catch (_) {
      return false;
    }
  });
  const attemptsByKeyRef = useRef(new Map());
  const attemptsRef = useRef([]);
  const passedRef = useRef(new Set());
  const mistakesRef = useRef(new Map());
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingUrlsRef = useRef(new Map());
  const recordingAudioRef = useRef(null);
  const busyRef = useRef(0);
  const startedRef = useRef(performance.now());
  const finishedRef = useRef(false);
  const mountedRef = useRef(true);
  const pronunciationWords = useMemo(() => collectPronunciationWords(allItems), [allItems]);
  const pronunciationWordMap = useMemo(() => new Map(
    pronunciationWords.map((word) => [pronunciationTokenKey(word), word]),
  ), [pronunciationWords]);

  const stopStream = () => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const updateCard = useCallback((key, updates) => {
    setBoard((current) => ({
      ...current,
      cards: updatePronunciationCard(current.cards, key, updates),
    }));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const sessionKey = allItems.map(pronunciationItemKey).join('|');
    beginAudioSession(`pronunciation-three-card:${sessionKey}`);
    prefetchSessionAudio(
      allItems.slice(0, 6).map((item) => ({ text: item.pronunciation_sentence, mode: 'sentence' })),
      { concurrency: 3, onProgress: setAudioProgress },
    );

    let cancelled = false;
    let completedWords = 0;
    let failedWords = 0;
    const totalWords = pronunciationWords.length;
    const reportWordProgress = () => {
      if (!cancelled) setWordAudioProgress({ done: completedWords, total: totalWords, failed: failedWords });
    };
    const waitForAvailableAudioLane = async () => {
      while (!cancelled && busyRef.current > 0) {
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
    };
    const warmWords = async () => {
      const initialWords = pronunciationWords.slice(0, 5).map((word) => ({ text: word, mode: 'word' }));
      const remainingWords = pronunciationWords.slice(5).map((word) => ({ text: word, mode: 'word' }));
      if (initialWords.length) {
        const initialResult = await prefetchSessionAudio(initialWords, {
          concurrency: 5,
          onProgress: (progress) => {
            completedWords = progress.done;
            failedWords = progress.failed;
            reportWordProgress();
          },
        });
        completedWords = initialResult.done;
        failedWords = initialResult.failed;
        reportWordProgress();
      }
      for (const entry of remainingWords) {
        if (cancelled) return;
        await waitForAvailableAudioLane();
        if (cancelled) return;
        const result = await prefetchSessionAudio([entry], { concurrency: 1, priority: 'low' });
        completedWords += result.done;
        failedWords += result.failed;
        reportWordProgress();
      }
    };
    warmWords().catch(() => {});
    return () => {
      cancelled = true;
      mountedRef.current = false;
      recorderRef.current?.state === 'recording' && recorderRef.current.stop();
      stopStream();
      skipAllTTS();
      disposeAudioSession();
      recordingAudioRef.current?.pause?.();
      recordingUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      recordingUrlsRef.current.clear();
    };
  }, [allItems, pronunciationWords]);

  useEffect(() => {
    const windowItems = [
      ...board.cards.map((card) => card.item),
      ...board.pending.slice(0, 3),
    ];
    const audioBusy = busyRef.current > 0;
    prefetchSessionAudio(windowItems.map((item) => ({
      text: item.pronunciation_sentence,
      mode: 'sentence',
    })), { concurrency: audioBusy ? 1 : 3, priority: audioBusy ? 'low' : undefined });
  }, [board.cards, board.pending]);

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const attempts = attemptsRef.current;
    const rounds = Math.max(1, ...attemptsByKeyRef.current.values());
    onFinish({
      mode: 'pronunciation',
      words: allItems.map(resultEntry),
      duration_ms: performance.now() - startedRef.current,
      first_pass_accuracy: allItems.length
        ? attempts.filter((attempt) => attempt.attempt_number === 1 && attempt.passed).length / allItems.length
        : 0,
      completion_rate: allItems.length ? passedRef.current.size / allItems.length : 0,
      rounds,
      average_response_ms: attempts.length
        ? attempts.reduce((sum, attempt) => sum + attempt.response_ms, 0) / attempts.length
        : 0,
      mistakes: [...mistakesRef.current.values()].map(resultEntry),
      attempts,
      max_streak: passedRef.current.size,
    });
  }, [allItems, onFinish]);

  useEffect(() => {
    if (
      allItems.length
      && board.cards.length === 0
      && board.pending.length === 0
      && passedRef.current.size === allItems.length
    ) finish();
  }, [allItems.length, board.cards.length, board.pending.length, finish]);

  const toggleWordTap = () => {
    const next = !wordTapEnabled;
    setWordTapEnabled(next);
    try {
      localStorage.setItem(PRONUNCIATION_WORD_TAP_KEY, next ? '1' : '0');
    } catch (_) {
      // Storage is optional; the control still works for this session.
    }
  };

  const playWordToken = (token) => {
    if (recordingKey || !token?.speakable) return;
    playTTS(pronunciationWordMap.get(token.key) || token.text, 'word');
  };

  const gradeBlob = async (item, blob, elapsedMs) => {
    const key = pronunciationItemKey(item);
    const attemptNumber = (attemptsByKeyRef.current.get(key) || 0) + 1;
    attemptsByKeyRef.current.set(key, attemptNumber);
    updateCard(key, { status: 'grading', error: '', feedback: null });
    try {
      const body = new FormData();
      body.append('audio', blob, `reading.${blob.type.includes('mp4') ? 'm4a' : 'webm'}`);
      body.append('word', item.word);
      body.append('sense_id', item.sense_id || 'primary');
      body.append('locale', locale);
      const response = await fetch('/api/student/self-check/pronunciation/grade', {
        method: 'POST',
        headers: authHeaders(false),
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Your recording could not be scored right now');
      const attempt = {
        word: item.word,
        sense_ids: [item.sense_id || 'primary'],
        correct: Boolean(data.passed),
        passed: Boolean(data.passed),
        score: data.score,
        spelling_correct: true,
        meaning_correct: true,
        timed_out: false,
        round: attemptNumber,
        attempt_number: attemptNumber,
        response_ms: elapsedMs,
      };
      attemptsRef.current.push(attempt);
      if (data.passed) {
        passedRef.current.add(key);
        mistakesRef.current.delete(key);
        setPassedCount(passedRef.current.size);
      } else {
        mistakesRef.current.set(key, item);
      }
      if (mountedRef.current) {
        updateCard(key, {
          status: data.passed ? 'passed' : 'failed',
          feedback: { ...data, attemptNumber },
          error: '',
        });
      }
    } catch (gradeError) {
      attemptsByKeyRef.current.set(key, Math.max(0, attemptNumber - 1));
      if (mountedRef.current) {
        updateCard(key, { status: 'failed', feedback: null, error: gradeError.message });
      }
    } finally {
      busyRef.current = Math.max(0, busyRef.current - 1);
    }
  };

  const rememberRecording = (key, blob) => {
    const previous = recordingUrlsRef.current.get(key);
    if (previous) URL.revokeObjectURL(previous);
    const url = URL.createObjectURL(blob);
    recordingUrlsRef.current.set(key, url);
    updateCard(key, { recordingUrl: url });
  };

  const startRecording = async (card) => {
    if (recordingKey || ['grading', 'passed'].includes(card.status)) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      updateCard(card.key, { error: 'Recording is not supported in this browser.' });
      return;
    }
    try {
      skipAllTTS();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = preferredRecordingType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];
      const startedAt = performance.now();
      const recordedItem = card.item;
      recorder.ondataavailable = (event) => event.data?.size && chunksRef.current.push(event.data);
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        stopStream();
        if (mountedRef.current) {
          setRecordingKey('');
          rememberRecording(card.key, blob);
          gradeBlob(recordedItem, blob, performance.now() - startedAt);
        }
      };
      recorder.start();
      busyRef.current += 1;
      setRecordingKey(card.key);
      updateCard(card.key, { status: 'recording', feedback: null, error: '' });
    } catch (_) {
      stopStream();
      updateCard(card.key, { status: 'failed', error: 'Microphone access is needed for pronunciation practice.' });
    }
  };

  const stopRecording = (key) => {
    if (recordingKey !== key) return;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  const confirmPassed = (key) => {
    const recordingUrl = recordingUrlsRef.current.get(key);
    if (recordingUrl) URL.revokeObjectURL(recordingUrl);
    recordingUrlsRef.current.delete(key);
    setBoard((current) => confirmPronunciationCard(current, key));
  };

  const renderSentence = (item) => {
    if (!wordTapEnabled) return item.pronunciation_sentence;
    return tokenizePronunciationSentence(item.pronunciation_sentence).map((token, tokenIndex) => (
      token.speakable ? (
        <button
          type="button"
          key={`${token.key}-${tokenIndex}`}
          onClick={() => playWordToken(token)}
          disabled={Boolean(recordingKey)}
          className="rounded-sm px-0.5 text-left text-indigo-800 underline decoration-indigo-200 decoration-2 underline-offset-4 hover:bg-indigo-50 hover:decoration-indigo-500 disabled:cursor-default disabled:opacity-60"
          title={t('Play this word')}
        >
          <span data-i18n-skip>{token.text}</span>
        </button>
      ) : <React.Fragment key={`text-${tokenIndex}`}><span data-i18n-skip>{token.text}</span></React.Fragment>
    ));
  };

  if (!allItems.length) {
    return <Card className="max-w-3xl mx-auto p-7"><p>{t('No pronunciation sentences are ready for this set yet.')}</p><button onClick={onExit} className="mt-4 border px-4 py-2 rounded-md">{t('Back')}</button></Card>;
  }

  return (
    <Card className="max-w-7xl mx-auto overflow-hidden">
      <div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-4">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-900" title={t('Back')}><FiArrowLeft /></button>
        <div className="text-center">
          <div className="font-semibold text-gray-900">{t('Pronunciation Practice')}</div>
          <div className="mt-0.5 text-sm text-gray-500">{t('Passed')} {passedCount}/{allItems.length} · {t('Waiting')} {board.pending.length}</div>
        </div>
        <button
          type="button"
          onClick={toggleWordTap}
          aria-pressed={wordTapEnabled}
          className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${wordTapEnabled ? 'border-indigo-300 bg-indigo-50 text-indigo-800' : 'border-gray-200 bg-white text-gray-700'}`}
        >
          <FiVolume2 /> {t('Tap words to listen')}
        </button>
      </div>
      {audioProgress.total > 0 && audioProgress.done < audioProgress.total && (
        <div className="h-1 bg-indigo-50"><div className="h-full bg-indigo-500 transition-[width]" style={{ width: `${(audioProgress.done / audioProgress.total) * 100}%` }} /></div>
      )}
      <div className="p-5 sm:p-7">
        {wordAudioProgress.total > 0 && wordAudioProgress.done < wordAudioProgress.total && (
          <div className="mb-5 max-w-sm" aria-live="polite">
            <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
              <span>{t('Preparing word audio')}</span>
              <span className="tabular-nums">{wordAudioProgress.done}/{wordAudioProgress.total}</span>
            </div>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full bg-indigo-500 transition-[width]" style={{ width: `${(wordAudioProgress.done / wordAudioProgress.total) * 100}%` }} />
            </div>
          </div>
        )}
        <p className="mb-5 text-sm text-gray-600">{t('Practice another card while one is being scored. Each sentence must pass on its own.')}</p>
        <div className="grid gap-5 lg:grid-cols-3">
          {board.cards.map((card) => {
            const isRecording = recordingKey === card.key;
            const isPassed = card.status === 'passed';
            const isFailed = card.status === 'failed';
            const isGrading = card.status === 'grading';
            const borderTone = isPassed
              ? 'border-emerald-300 bg-emerald-50/60'
              : isFailed
                ? 'border-amber-300 bg-amber-50/50'
                : isRecording
                  ? 'border-rose-300 bg-rose-50/40'
                  : isGrading
                    ? 'border-indigo-300 bg-indigo-50/40'
                    : 'border-gray-200 bg-white';
            return (
              <section key={card.key} className={`flex min-h-[34rem] flex-col rounded-lg border p-5 transition-colors ${borderTone}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase text-indigo-600">{t('Target word')}</div>
                    <h2 className="mt-1 text-2xl font-bold" data-i18n-skip>{card.item.display_label || card.item.word}</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => playTTS(card.item.pronunciation_sentence, 'sentence')}
                    disabled={Boolean(recordingKey)}
                    className="shrink-0 rounded-md border bg-white p-2 text-indigo-700 disabled:opacity-40"
                    title={t('Replay sentence')}
                  ><FiVolume2 /></button>
                </div>

                <p className="mt-5 text-lg leading-8 text-gray-900" data-i18n-skip>{renderSentence(card.item)}</p>
                {wordTapEnabled && <p className="mt-2 text-xs text-indigo-700">{t('Tap any word in the sentence to hear it.')}</p>}

                <div className="mt-5">
                  {isGrading ? (
                    <div className="inline-flex items-center gap-2 rounded-md bg-indigo-100 px-4 py-3 font-semibold text-indigo-800">
                      <FiRefreshCw className="animate-spin" /> {t('Scoring your reading...')}
                    </div>
                  ) : isRecording ? (
                    <button onClick={() => stopRecording(card.key)} className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-3 font-semibold text-white"><FiSquare /> {t('Finish recording')}</button>
                  ) : isPassed ? (
                    <button onClick={() => confirmPassed(card.key)} className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-3 font-semibold text-white"><FiCheck /> {t('Confirm and continue')}</button>
                  ) : (
                    <button
                      onClick={() => startRecording(card)}
                      disabled={Boolean(recordingKey)}
                      className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-3 font-semibold text-white disabled:opacity-40"
                    ><FiMic /> {t(isFailed ? 'Record again' : 'Start recording')}</button>
                  )}
                </div>

                {card.error && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{t(card.error)}</div>}
                {card.feedback && (
                  <div className={`mt-5 rounded-md border p-4 ${isPassed ? 'border-emerald-200 bg-white/80' : 'border-amber-200 bg-white/80'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className={`font-bold ${isPassed ? 'text-emerald-800' : 'text-amber-900'}`}>{t(isPassed ? 'Passed' : 'Try once more')}</h3>
                      <strong className="text-2xl tabular-nums">{card.feedback.score}/100</strong>
                    </div>
                    <p className="mt-3 text-sm text-gray-800" data-i18n-skip>{card.feedback.feedback}</p>
                    {!isPassed && card.feedback.issues?.length > 0 && (
                      <div className="mt-4 space-y-3 border-t border-amber-200 pt-4">
                        {card.feedback.issues.map((issue, issueIndex) => (
                          <div key={`${issue.segment}-${issueIndex}`} className="text-sm" data-i18n-skip>
                            <div className="font-semibold text-gray-950">{issue.segment}</div>
                            <div className="mt-1 text-gray-700">{issue.problem}</div>
                            <div className="mt-1 text-indigo-800">{issue.correction}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="mt-3 text-xs text-gray-600" data-i18n-skip>{card.feedback.next_step}</p>
                    {card.recordingUrl && (
                      <button type="button" onClick={() => playRecording(recordingAudioRef, card.recordingUrl)} className="mt-4 inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800">
                        <FiPlay /> {t('Listen to my recording')}
                      </button>
                    )}
                  </div>
                )}
                <div className="mt-auto pt-5 text-xs text-gray-500">
                  {isPassed ? t('Confirm when you are ready for the next sentence.') : isGrading ? t('You can practise either of the other cards now.') : t('Listen once, then read the sentence aloud. A clear target word matters most.')}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

const PronunciationRunner = ({ items, onFinish, onExit }) => (
  <PronunciationBoard
    items={items}
    onFinish={onFinish}
    onExit={onExit}
    playAudio={playTTS}
    stopAudio={skipAllTTS}
  />
);

const LegacyDictationRunner = ({ items, onFinish, onExit }) => {
  const { t } = useI18n();
  const [queue, setQueue] = useState(() => shuffle(items));
  const [index, setIndex] = useState(0);
  const [round, setRound] = useState(1);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const inputRef = useRef(null);
  const startRef = useRef(performance.now());
  const questionStartRef = useRef(performance.now());
  const attemptsRef = useRef([]);
  const firstCorrectRef = useRef(new Set());
  const allMistakesRef = useRef(new Set());
  const roundMistakesRef = useRef([]);
  const current = queue[index];
  const currentWord = current?.word;

  useEffect(() => {
    questionStartRef.current = performance.now();
    setAnswer('');
    setFeedback(null);
    if (currentWord) {
      playTTS(currentWord, 'word');
      window.setTimeout(() => inputRef.current?.focus(), 80);
    }
    return () => skipAllTTS();
  }, [currentWord, round]);

  const advance = useCallback(() => {
    if (index + 1 < queue.length) {
      setIndex((value) => value + 1);
      return;
    }
    if (roundMistakesRef.current.length) {
      const next = shuffle(roundMistakesRef.current);
      roundMistakesRef.current = [];
      setQueue(next);
      setIndex(0);
      setRound((value) => value + 1);
      return;
    }
    const elapsed = performance.now() - startRef.current;
    onFinish({
      mode: 'dictation',
      words: items.map((item) => item.word),
      duration_ms: elapsed,
      first_pass_accuracy: firstCorrectRef.current.size / items.length,
      rounds: round,
      average_response_ms: attemptsRef.current.length ? attemptsRef.current.reduce((sum, item) => sum + item.response_ms, 0) / attemptsRef.current.length : 0,
      mistakes: [...allMistakesRef.current],
      attempts: attemptsRef.current,
    });
  }, [index, items, onFinish, queue.length, round]);

  const submit = (event) => {
    event.preventDefault();
    if (!current || feedback || !answer.trim()) return;
    const correct = normalizeAnswer(answer) === normalizeAnswer(current.word);
    const attempt = { word: current.word, correct, timed_out: false, round, response_ms: performance.now() - questionStartRef.current };
    attemptsRef.current.push(attempt);
    if (correct && round === 1) firstCorrectRef.current.add(current.word);
    if (!correct) {
      allMistakesRef.current.add(current.word);
      if (!roundMistakesRef.current.some((item) => item.word === current.word)) roundMistakesRef.current.push(current);
      setFeedback({ answer, parts: diffCharacters(current.word, answer) });
    } else {
      setFeedback({ correct: true });
      window.setTimeout(advance, 320);
    }
  };

  if (!current) return null;
  return (
    <Card className="max-w-3xl mx-auto overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-900"><FiArrowLeft /></button>
        <div className="text-sm text-gray-600">{t('Round')} {round} · {index + 1}/{queue.length}</div>
        <button onClick={() => playTTS(current.word, 'word')} className="p-2 border rounded-md text-indigo-700" title={t('Replay word')}><FiVolume2 /></button>
      </div>
      <div className="p-6 sm:p-10 text-center">
        <FiHeadphones className="mx-auto text-indigo-600" size={34} />
        <div className="mt-5 text-lg text-gray-700" data-i18n-skip>{current.definition_cn || current.definition_en}</div>
        {current.pos && <div className="mt-2 text-sm text-gray-400" data-i18n-skip>{current.pos}</div>}
        <form onSubmit={submit} className="mt-7">
          <input ref={inputRef} value={answer} onChange={(event) => setAnswer(event.target.value)} disabled={Boolean(feedback)} autoComplete="off" spellCheck="false" className="w-full max-w-lg text-center text-2xl border-b-2 border-gray-300 px-3 py-3 focus:border-indigo-600 focus:outline-none bg-transparent" placeholder={t('Type what you hear')} data-i18n-skip />
          {!feedback && <button className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-md font-semibold">{t('Check spelling')}</button>}
        </form>
        {feedback?.correct && <div className="mt-6 text-emerald-700 font-semibold inline-flex items-center gap-2"><FiCheck /> {t('Correct')}</div>}
        {feedback && !feedback.correct && (
          <div className="mt-7 border border-rose-200 bg-rose-50 rounded-lg p-5 text-left">
            <div className="font-semibold text-rose-800">{t('Look closely, then try it again next round.')}</div>
            <div className="mt-3 font-mono text-2xl tracking-wide" data-i18n-skip>
              {feedback.parts.map((part, partIndex) => <span key={`${partIndex}-${part.value}`} className={part.type === 'match' ? 'text-gray-900' : part.type === 'missing' ? 'text-rose-600 underline' : 'text-amber-600 line-through'}>{part.value}</span>)}
            </div>
            <div className="mt-2 text-sm text-gray-600">{t('Correct spelling:')} <strong data-i18n-skip>{current.word}</strong></div>
            <button onClick={advance} className="mt-4 px-5 py-2 bg-gray-900 text-white rounded-md">{t('Continue')}</button>
          </div>
        )}
      </div>
    </Card>
  );
};

const LegacyMeaningRunner = ({ items, distractorPool, sudden, onFinish, onExit }) => {
  const { t } = useI18n();
  const [queue, setQueue] = useState(() => shuffle(items));
  const [index, setIndex] = useState(0);
  const [round, setRound] = useState(1);
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const lockedRef = useRef(false);
  const startRef = useRef(performance.now());
  const questionStartRef = useRef(performance.now());
  const attemptsRef = useRef([]);
  const firstCorrectRef = useRef(new Set());
  const allMistakesRef = useRef(new Set());
  const roundMistakesRef = useRef([]);
  const minLimitRef = useRef(5000);
  const current = queue[index];
  const limitMs = sudden ? suddenTimeLimit(streak) : 5000;
  minLimitRef.current = Math.min(minLimitRef.current, limitMs);
  const choices = useMemo(() => current ? buildMeaningChoices(current, items, distractorPool) : [], [current, items, distractorPool]);

  useEffect(() => {
    lockedRef.current = false;
    setFeedback(null);
    questionStartRef.current = performance.now();
  }, [current?.word, round]);

  const finish = useCallback((finalStreak = streak) => {
    onFinish({
      mode: sudden ? 'sudden_sprint' : 'meaning_sprint',
      words: items.map((item) => item.word),
      duration_ms: performance.now() - startRef.current,
      first_pass_accuracy: firstCorrectRef.current.size / items.length,
      rounds: round,
      average_response_ms: attemptsRef.current.length ? attemptsRef.current.reduce((sum, item) => sum + item.response_ms, 0) / attemptsRef.current.length : 0,
      max_streak: finalStreak,
      minimum_limit_ms: minLimitRef.current,
      mistakes: [...allMistakesRef.current],
      attempts: attemptsRef.current,
    });
  }, [items, onFinish, round, streak, sudden]);

  const moveNext = useCallback((wasCorrect) => {
    if (sudden && !wasCorrect) {
      finish(streak);
      return;
    }
    if (index + 1 < queue.length) {
      setIndex((value) => value + 1);
      return;
    }
    if (!sudden && roundMistakesRef.current.length) {
      const next = shuffle(roundMistakesRef.current);
      roundMistakesRef.current = [];
      setQueue(next);
      setIndex(0);
      setRound((value) => value + 1);
      return;
    }
    if (sudden) {
      const lastWord = current.word;
      const reshuffled = shuffle(items);
      if (reshuffled.length > 1 && reshuffled[0].word === lastWord) [reshuffled[0], reshuffled[1]] = [reshuffled[1], reshuffled[0]];
      setQueue(reshuffled);
      setIndex(0);
      setRound((value) => value + 1);
      return;
    }
    finish(streak);
  }, [current?.word, finish, index, items, queue.length, streak, sudden]);

  const answerQuestion = useCallback((choice, timedOut = false) => {
    if (!current || lockedRef.current) return;
    lockedRef.current = true;
    const expected = current.definition_cn || current.definition_en;
    const correct = !timedOut && choice === expected;
    const responseMs = Math.min(limitMs, performance.now() - questionStartRef.current);
    attemptsRef.current.push({ word: current.word, correct, timed_out: timedOut, round, response_ms: responseMs });
    if (correct && round === 1) firstCorrectRef.current.add(current.word);
    if (!correct) {
      allMistakesRef.current.add(current.word);
      if (!roundMistakesRef.current.some((item) => item.word === current.word)) roundMistakesRef.current.push(current);
    }
    const nextStreak = sudden && correct ? streak + 1 : streak;
    if (sudden && correct) setStreak(nextStreak);
    setFeedback({ choice, expected, correct, timedOut });
    window.setTimeout(() => moveNext(correct), sudden && !correct ? 900 : 620);
  }, [current, limitMs, moveNext, round, streak, sudden]);

  const remaining = useDeadline(limitMs, `${current?.word}-${round}-${index}`, !feedback, () => answerQuestion(null, true));
  if (!current) return null;
  const progress = Math.max(0, Math.min(100, (remaining / limitMs) * 100));
  return (
    <Card className="max-w-4xl mx-auto overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-900"><FiArrowLeft /></button>
        <div className="text-sm text-gray-600">{sudden ? `${t('Streak')} ${streak}` : `${t('Round')} ${round} · ${index + 1}/${queue.length}`}</div>
        <div className="font-mono font-bold text-indigo-700">{(remaining / 1000).toFixed(1)}s</div>
      </div>
      <div className="h-2 bg-gray-100"><div className={`h-full transition-colors ${progress < 25 ? 'bg-rose-500' : 'bg-indigo-600'}`} style={{ width: `${progress}%` }} /></div>
      <div className="p-6 sm:p-10">
        <div className="text-center">
          {sudden ? <FiZap className="mx-auto text-amber-500" size={30} /> : <FiClock className="mx-auto text-indigo-600" size={30} />}
          <div className="mt-4 text-sm uppercase font-semibold text-gray-500">{t('Choose the closest meaning')}</div>
          <h2 className="mt-2 text-4xl font-bold text-gray-900" data-i18n-skip>{current.word}</h2>
          {current.pos && <div className="mt-2 text-sm text-gray-400" data-i18n-skip>{current.pos}</div>}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-8">
          {choices.map((choice) => {
            const isExpected = feedback && choice === feedback.expected;
            const isWrong = feedback && choice === feedback.choice && !feedback.correct;
            return (
              <button key={choice} disabled={Boolean(feedback)} onClick={() => answerQuestion(choice)} className={`min-h-[72px] border-2 rounded-lg px-4 py-3 text-left font-medium ${isExpected ? 'border-emerald-500 bg-emerald-50' : isWrong ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-indigo-400'}`} data-i18n-skip>
                {choice}
              </button>
            );
          })}
        </div>
        {feedback && <div className={`mt-5 text-center font-semibold ${feedback.correct ? 'text-emerald-700' : 'text-rose-700'}`}>{feedback.correct ? t('Correct') : feedback.timedOut ? t('Time is up') : t('Not this one')}</div>}
      </div>
    </Card>
  );
};

const resultEntry = (group) => ({
  word: group.word,
  sense_ids: group.sense_ids,
  display_label: group.display_label,
});

const MeaningChoices = ({ choices, selected, feedback, onToggle, multiple = false }) => (
  <div className="grid sm:grid-cols-2 gap-3 mt-6">
    {choices.map((choice) => {
      const isSelected = selected.has(choice.key);
      const expected = feedback && choice.correct;
      const selectedWrong = feedback && isSelected && !choice.correct;
      return (
        <button
          type="button"
          key={choice.key}
          disabled={Boolean(feedback)}
          onClick={() => onToggle(choice.key)}
          aria-pressed={isSelected}
          className={`min-h-[68px] border-2 rounded-lg px-4 py-3 text-left font-medium ${
            expected ? 'border-emerald-500 bg-emerald-50'
              : selectedWrong ? 'border-rose-500 bg-rose-50'
                : isSelected ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-indigo-400'
          }`}
          data-i18n-skip
        >
          <span className="inline-flex items-start gap-2">
            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border ${multiple ? '' : 'rounded-full'} ${isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300'}`}>
              {isSelected && <FiCheck size={14} />}
            </span>
            {choice.label}
          </span>
        </button>
      );
    })}
  </div>
);

const DictationRunner = ({ items, distractorPool, onFinish, onExit }) => {
  const { t } = useI18n();
  const groups = useMemo(() => groupSelfCheckItems(items), [items]);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [round, setRound] = useState(1);
  const [answer, setAnswer] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [feedback, setFeedback] = useState(null);
  const [audioUrls, setAudioUrls] = useState(null);
  const [audioProgress, setAudioProgress] = useState({ done: 0, total: groups.length });
  const [audioError, setAudioError] = useState('');
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const startRef = useRef(performance.now());
  const questionStartRef = useRef(performance.now());
  const attemptsRef = useRef([]);
  const firstCorrectRef = useRef(new Set());
  const allMistakesRef = useRef(new Map());
  const roundMistakesRef = useRef([]);
  const current = queue[index];
  const choices = useMemo(
    () => current ? buildGroupMeaningChoices(current, items, distractorPool) : [],
    [current, items, distractorPool],
  );

  useEffect(() => {
    let active = true;
    let createdUrls;
    prefetchWordAudio(
      groups.map((group) => group.word),
      (done, total) => active && setAudioProgress({ done, total }),
    ).then((urls) => {
      createdUrls = urls;
      if (!active) return;
      setAudioUrls(urls);
      setQueue(shuffle(groups));
      startRef.current = performance.now();
    }).catch((error) => {
      if (active) setAudioError(error.message);
    });
    return () => {
      active = false;
      audioRef.current?.pause();
      createdUrls?.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [groups]);

  useEffect(() => {
    if (!current || !audioUrls) return;
    questionStartRef.current = performance.now();
    setAnswer('');
    setSelected(new Set());
    setFeedback(null);
    playPreparedAudio(audioRef, audioUrls.get(current.word.toLocaleLowerCase()));
    window.setTimeout(() => inputRef.current?.focus(), 80);
  }, [current, audioUrls, round]);

  const advance = useCallback(() => {
    if (index + 1 < queue.length) {
      setIndex((value) => value + 1);
      return;
    }
    if (roundMistakesRef.current.length) {
      const next = shuffle(roundMistakesRef.current);
      roundMistakesRef.current = [];
      setQueue(next);
      setIndex(0);
      setRound((value) => value + 1);
      return;
    }
    onFinish({
      mode: 'dictation',
      words: groups.map(resultEntry),
      duration_ms: performance.now() - startRef.current,
      first_pass_accuracy: firstCorrectRef.current.size / groups.length,
      rounds: round,
      average_response_ms: attemptsRef.current.length
        ? attemptsRef.current.reduce((sum, item) => sum + item.response_ms, 0) / attemptsRef.current.length
        : 0,
      mistakes: [...allMistakesRef.current.values()].map(resultEntry),
      attempts: attemptsRef.current,
    });
  }, [groups, index, onFinish, queue.length, round]);

  const multipleMeanings = (current?.senses?.length || 0) > 1;
  const toggleChoice = (key) => setSelected(
    (previous) => updateMeaningSelection(previous, key, multipleMeanings),
  );

  const submit = (event) => {
    event.preventDefault();
    if (!current || feedback || !answer.trim() || !selected.size) return;
    const expected = new Set(choices.filter((choice) => choice.correct).map((choice) => choice.key));
    const spellingCorrect = normalizeAnswer(answer) === normalizeAnswer(current.word);
    const meaningCorrect = selected.size === expected.size
      && [...selected].every((key) => expected.has(key));
    const correct = spellingCorrect && meaningCorrect;
    attemptsRef.current.push({
      word: current.word,
      sense_ids: current.sense_ids,
      correct,
      spelling_correct: spellingCorrect,
      meaning_correct: meaningCorrect,
      timed_out: false,
      round,
      response_ms: performance.now() - questionStartRef.current,
    });
    const identity = groupIdentity(current);
    if (correct && round === 1) firstCorrectRef.current.add(identity);
    if (!correct) {
      allMistakesRef.current.set(identity, current);
      if (!roundMistakesRef.current.some((item) => groupIdentity(item) === identity)) {
        roundMistakesRef.current.push(current);
      }
    }
    setFeedback({ correct, spellingCorrect, meaningCorrect, parts: diffCharacters(current.word, answer) });
    if (correct) window.setTimeout(advance, 420);
  };

  if (!audioUrls) {
    const percent = audioProgress.total ? Math.round((audioProgress.done / audioProgress.total) * 100) : 0;
    return (
      <Card className="max-w-3xl mx-auto p-8 text-center">
        <FiHeadphones className="mx-auto text-indigo-600" size={36} />
        <h2 className="mt-4 text-xl font-bold">{t('Preparing dictation audio')}</h2>
        <p className="mt-2 text-gray-600">{audioError || `${audioProgress.done}/${audioProgress.total}`}</p>
        <div className="mx-auto mt-5 h-2 max-w-lg overflow-hidden bg-gray-100">
          <div className="h-full bg-indigo-600 transition-all" style={{ width: `${percent}%` }} />
        </div>
        {audioError && <button onClick={onExit} className="mt-5 border px-4 py-2">{t('Back')}</button>}
      </Card>
    );
  }
  if (!current) return null;
  return (
    <Card className="max-w-3xl mx-auto overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-900"><FiArrowLeft /></button>
        <div className="text-sm text-gray-600">{t('Round')} {round} · {index + 1}/{queue.length}</div>
        <button onClick={() => playPreparedAudio(audioRef, audioUrls.get(current.word.toLocaleLowerCase()))} className="p-2 border rounded-md text-indigo-700" title={t('Replay word')}><FiVolume2 /></button>
      </div>
      <DictationQuestion
        word={current.word}
        choices={choices}
        answer={answer}
        selected={selected}
        feedback={feedback}
        onAnswerChange={setAnswer}
        onToggleChoice={toggleChoice}
        onSubmit={submit}
        onAdvance={advance}
        onReplay={() => playPreparedAudio(audioRef, audioUrls.get(current.word.toLocaleLowerCase()))}
        inputRef={inputRef}
        showIntro
        showReplay={false}
        multiple={multipleMeanings}
        inputId="self-check-dictation"
      />
    </Card>
  );
};

const MeaningRunner = ({ items, distractorPool, sudden, onFinish, onExit }) => {
  const { t } = useI18n();
  const groups = useMemo(() => groupSelfCheckItems(items), [items]);
  const [queue, setQueue] = useState(() => shuffle(groups));
  const [index, setIndex] = useState(0);
  const [round, setRound] = useState(1);
  const [streak, setStreak] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [feedback, setFeedback] = useState(null);
  const lockedRef = useRef(false);
  const startRef = useRef(performance.now());
  const questionStartRef = useRef(performance.now());
  const attemptsRef = useRef([]);
  const firstCorrectRef = useRef(new Set());
  const allMistakesRef = useRef(new Map());
  const roundMistakesRef = useRef([]);
  const minLimitRef = useRef(MEANING_SPRINT_BASE_MS);
  const current = queue[index];
  const baseLimit = sudden ? suddenTimeLimit(streak) : MEANING_SPRINT_BASE_MS;
  const limitMs = meaningTimeLimit(current, baseLimit);
  minLimitRef.current = Math.min(minLimitRef.current, limitMs);
  const choices = useMemo(
    () => current ? buildGroupMeaningChoices(current, items, distractorPool) : [],
    [current, items, distractorPool],
  );

  useEffect(() => {
    lockedRef.current = false;
    setSelected(new Set());
    setFeedback(null);
    questionStartRef.current = performance.now();
  }, [current, round]);

  const finish = useCallback((finalStreak = streak) => {
    onFinish({
      mode: sudden ? 'sudden_sprint' : 'meaning_sprint',
      words: groups.map(resultEntry),
      duration_ms: performance.now() - startRef.current,
      first_pass_accuracy: firstCorrectRef.current.size / groups.length,
      rounds: round,
      average_response_ms: attemptsRef.current.length
        ? attemptsRef.current.reduce((sum, item) => sum + item.response_ms, 0) / attemptsRef.current.length
        : 0,
      max_streak: finalStreak,
      minimum_limit_ms: minLimitRef.current,
      mistakes: [...allMistakesRef.current.values()].map(resultEntry),
      attempts: attemptsRef.current,
    });
  }, [groups, onFinish, round, streak, sudden]);

  const moveNext = useCallback((wasCorrect) => {
    if (sudden && !wasCorrect) {
      finish(streak);
      return;
    }
    if (index + 1 < queue.length) {
      setIndex((value) => value + 1);
      return;
    }
    if (!sudden && roundMistakesRef.current.length) {
      const next = shuffle(roundMistakesRef.current);
      roundMistakesRef.current = [];
      setQueue(next);
      setIndex(0);
      setRound((value) => value + 1);
      return;
    }
    if (sudden) {
      const lastKey = groupIdentity(current);
      const reshuffled = shuffle(groups);
      if (reshuffled.length > 1 && groupIdentity(reshuffled[0]) === lastKey) {
        [reshuffled[0], reshuffled[1]] = [reshuffled[1], reshuffled[0]];
      }
      setQueue(reshuffled);
      setIndex(0);
      setRound((value) => value + 1);
      return;
    }
    finish(streak);
  }, [current, finish, groups, index, queue.length, streak, sudden]);

  const answerQuestion = useCallback((timedOut = false) => {
    if (!current || lockedRef.current) return;
    lockedRef.current = true;
    const expected = new Set(choices.filter((choice) => choice.correct).map((choice) => choice.key));
    const selectedMatchesExpected = selected.size === expected.size
      && [...selected].every((key) => expected.has(key));
    const correct = selectedMatchesExpected && (!timedOut || !sudden);
    attemptsRef.current.push({
      word: current.word,
      sense_ids: current.sense_ids,
      correct,
      spelling_correct: true,
      meaning_correct: correct,
      timed_out: timedOut,
      round,
      response_ms: Math.min(limitMs, performance.now() - questionStartRef.current),
    });
    const identity = groupIdentity(current);
    if (correct && round === 1) firstCorrectRef.current.add(identity);
    if (!correct) {
      allMistakesRef.current.set(identity, current);
      if (!roundMistakesRef.current.some((item) => groupIdentity(item) === identity)) {
        roundMistakesRef.current.push(current);
      }
    }
    const nextStreak = sudden && correct ? streak + 1 : streak;
    if (sudden && correct) setStreak(nextStreak);
    setFeedback({ correct, timedOut });
    window.setTimeout(() => moveNext(correct), sudden && !correct ? 900 : 620);
  }, [choices, current, limitMs, moveNext, round, selected, streak, sudden]);

  const remaining = useDeadline(limitMs, `${groupIdentity(current || {})}-${round}-${index}`, !feedback, () => answerQuestion(true));
  if (!current) return null;
  const progress = Math.max(0, Math.min(100, (remaining / limitMs) * 100));
  const multipleMeaningChoices = current.senses.length > 1;
  const toggleChoice = (key) => setSelected(
    (previous) => updateMeaningSelection(previous, key, multipleMeaningChoices),
  );
  return (
    <Card className="max-w-4xl mx-auto overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-900"><FiArrowLeft /></button>
        <div className="text-sm text-gray-600">{sudden ? `${t('Streak')} ${streak}` : `${t('Round')} ${round} · ${index + 1}/${queue.length}`}</div>
        <div className="font-mono font-bold text-indigo-700">{(remaining / 1000).toFixed(1)}s</div>
      </div>
      <div className="h-2 bg-gray-100"><div className={`h-full transition-colors ${progress < 25 ? 'bg-rose-500' : 'bg-indigo-600'}`} style={{ width: `${progress}%` }} /></div>
      <div className="p-6 sm:p-10">
        <div className="text-center">
          {sudden ? <FiZap className="mx-auto text-amber-500" size={30} /> : <FiClock className="mx-auto text-indigo-600" size={30} />}
          <div className="mt-4 text-sm uppercase font-semibold text-gray-500">{current.senses.length > 1 ? t('Choose every matching meaning') : t('Choose the closest meaning')}</div>
          <h2 className="mt-2 text-4xl font-bold text-gray-900" data-i18n-skip>{current.word}</h2>
          {current.senses.length > 1 && <div className="mt-2 text-sm text-indigo-700">{current.senses.length} {t('meanings')}</div>}
        </div>
        <MeaningChoices choices={choices} selected={selected} feedback={feedback} onToggle={toggleChoice} multiple={multipleMeaningChoices} />
        {!feedback && <div className="mt-5 text-center"><button disabled={!selected.size} onClick={() => answerQuestion(false)} className="bg-indigo-600 px-6 py-3 font-semibold text-white disabled:opacity-50">{t('Confirm meanings')}</button></div>}
        {feedback && <div className={`mt-5 text-center font-semibold ${feedback.correct ? 'text-emerald-700' : 'text-rose-700'}`}>{feedback.correct ? t('Correct') : feedback.timedOut ? t('Time is up') : t('Not quite')}</div>}
      </div>
    </Card>
  );
};

const Results = ({ result, saving, onBackToStart, onRestart, onRetry, onAddMistakes }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const modeName = result.mode === 'dictation' ? 'Dictation Loop' : result.mode === 'meaning_sprint' ? '7-Second Meaning' : result.mode === 'pronunciation' ? 'Pronunciation Practice' : 'Lightning Challenge';
  const isPronunciation = result.mode === 'pronunciation';
  const headlineRate = isPronunciation ? (result.completion_rate ?? 0) : result.first_pass_accuracy;
  const cleanFirstPass = result.first_pass_accuracy >= 1;
  return (
    <Card className="max-w-4xl mx-auto p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
        <div><div className="text-xs uppercase font-semibold text-indigo-600">{t('Self-check complete')}</div><h2 className="text-3xl font-bold mt-1">{t(modeName)}</h2><p className="text-gray-500 mt-2">{saving ? t('Saving result...') : t('Saved to your private practice history')}</p></div>
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-5 py-4 text-center"><div className="text-3xl font-bold text-indigo-700">{Math.round(headlineRate * 100)}%</div><div className="text-xs uppercase text-indigo-700">{t(isPronunciation ? 'Passed' : 'First pass')}</div></div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-7">
        <div className="border rounded-lg p-4"><div className="text-sm text-gray-500">{t('Time')}</div><div className="text-xl font-bold">{formatDuration(result.duration_ms)}</div></div>
        <div className="border rounded-lg p-4"><div className="text-sm text-gray-500">{t('Rounds')}</div><div className="text-xl font-bold">{result.rounds}</div></div>
        <div className="border rounded-lg p-4"><div className="text-sm text-gray-500">{t('Average response')}</div><div className="text-xl font-bold">{result.average_response_ms ? `${(result.average_response_ms / 1000).toFixed(1)}s` : '—'}</div></div>
        <div className="border rounded-lg p-4"><div className="text-sm text-gray-500">{t(result.mode === 'sudden_sprint' ? 'Best streak' : 'Words checked')}</div><div className="text-xl font-bold">{result.mode === 'sudden_sprint' ? result.max_streak : result.words.length}</div></div>
      </div>
      <div className="mt-7 border rounded-lg p-5">
        <h3 className="font-bold">{result.mistakes.length ? t('Words worth another look') : t(cleanFirstPass ? 'Clean run' : 'All words passed')}</h3>
        {result.mistakes.length ? <div className="flex flex-wrap gap-2 mt-3">{result.mistakes.map((item) => {
          const label = typeof item === 'string' ? item : (item.display_label || item.word);
          const key = typeof item === 'string' ? item : `${item.word}-${(item.sense_ids || []).join('-')}`;
          return <span key={key} className="bg-amber-50 border border-amber-200 rounded-md px-3 py-1 text-sm" data-i18n-skip>{label}</span>;
        })}</div> : <p className="text-emerald-700 mt-2">{t(cleanFirstPass ? 'You cleared every word without a first-pass miss.' : 'You passed every word after another round of practice.')}</p>}
      </div>
      <div className="mt-7 flex flex-wrap gap-3">
        <button onClick={onRestart} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md"><FiRotateCcw /> {t(result.mode === 'sudden_sprint' ? 'Restart challenge' : 'Repeat full test')}</button>
        {result.mistakes.length > 0 && <button onClick={onRetry} className="inline-flex items-center gap-2 px-4 py-2 border rounded-md"><FiRefreshCw /> {t('Retry missed words')}</button>}
        {result.mistakes.length > 0 && <button onClick={onAddMistakes} className="inline-flex items-center gap-2 px-4 py-2 border rounded-md"><FiPlus /> {t('Add missed words to learning')}</button>}
        <button onClick={onBackToStart} className="inline-flex items-center gap-2 px-4 py-2 border rounded-md"><FiEdit3 /> {t('Back to word selection')}</button>
        <button onClick={() => navigate('/student/dashboard')} className="inline-flex items-center gap-2 px-4 py-2 border rounded-md"><FiBookOpen /> {t('Dashboard')}</button>
      </div>
    </Card>
  );
};

export default function SelfCheckPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageTopRef = useRef(null);
  const postLearningDictationStartedRef = useRef(false);
  const postLearningModesRef = useRef([]);
  const [phase, setPhase] = useState('build');
  const [experimentsOpen, setExperimentsOpen] = useState(false);
  const [sources, setSources] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Shanghai' }));
  const [text, setText] = useState('');
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [distractorPool, setDistractorPool] = useState([]);
  const [unresolved, setUnresolved] = useState([]);
  const [history, setHistory] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const assignmentId = searchParams.get('assignment_id') || '';
  const assignmentTargetId = searchParams.get('target_id') || '';
  const assignmentAttemptId = searchParams.get('attempt_id') || '';
  const assignmentMode = searchParams.get('mode') || '';
  const assignmentLoadRef = useRef('');

  const loadRecentHistory = useCallback(async () => {
    const [selfCheckResult, ieltsResult] = await Promise.allSettled([
      fetch('/api/student/self-check/sessions?limit=10', { headers: authHeaders() })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error())),
      fetchIeltsSpeakingHistory({ limit: 10 }),
    ]);
    const sessions = selfCheckResult.status === 'fulfilled'
      ? (selfCheckResult.value.sessions || [])
      : [];
    const speaking = ieltsResult.status === 'fulfilled'
      ? ieltsResult.value
        .filter((attempt) => attempt.recording_id)
        .map((attempt) => ({
          ...attempt,
          id: attempt.attempt_id || attempt.id,
          mode: 'ielts_speaking',
        }))
      : [];
    setHistory([...sessions, ...speaking]
      .sort((left, right) => new Date(right.created_at || 0) - new Date(left.created_at || 0))
      .slice(0, 10));
  }, []);

  const loadSources = useCallback(async (date) => {
    try {
      const response = await fetch(`/api/student/self-check/sources?date=${encodeURIComponent(date)}`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Could not load study history');
      setSources(await response.json());
      setError('');
    } catch (loadError) {
      setError(loadError.message);
    }
  }, []);

  useEffect(() => { loadSources(selectedDate); }, [loadSources, selectedDate]);
  useEffect(() => {
    pageTopRef.current?.scrollIntoView({ block: 'start' });
  }, [phase]);
  useEffect(() => { void loadRecentHistory(); }, [loadRecentHistory]);
  useEffect(() => {
    const isLegacy = searchParams.get('post_learning_dictation') === '1';
    const isFollowup = searchParams.get('post_learning_followup') === '1';
    if ((!isLegacy && !isFollowup) || postLearningDictationStartedRef.current) {
      return;
    }
    postLearningDictationStartedRef.current = true;
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(
        isFollowup ? 'lexilab.post-learning-followup' : 'lexilab.post-learning-dictation',
      ) || 'null');
    } catch (_) {
      saved = null;
    }
    const words = Array.isArray(saved?.words) ? saved.words.filter(Boolean) : [];
    const modes = isFollowup && Array.isArray(saved?.modes)
      ? saved.modes.filter((mode) => ['dictation', 'pronunciation'].includes(mode))
      : ['dictation'];
    if (!words.length) {
      setError('The saved dictation set is no longer available. Choose the words again below.');
      navigate('/student/self-check', { replace: true });
      return;
    }

    setResolving(true);
    setError('');
    fetch('/api/student/self-check/resolve', {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ words }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Could not prepare this dictation');
        if (!data.items?.length) throw new Error('None of these words were found in the dictionary');
        setItems(data.items);
        setOriginalItems(data.items);
        setDistractorPool(data.distractor_pool || []);
        setUnresolved(data.unresolved || []);
        postLearningModesRef.current = modes.length ? [...modes] : ['dictation'];
        setPhase(postLearningModesRef.current[0]);
        sessionStorage.removeItem(isFollowup ? 'lexilab.post-learning-followup' : 'lexilab.post-learning-dictation');
        navigate('/student/self-check', { replace: true });
      })
      .catch((resumeError) => {
        setText(words.join('\n'));
        setError(resumeError.message || 'Could not prepare this dictation');
      })
      .finally(() => setResolving(false));
  }, [navigate, searchParams]);

  useEffect(() => {
    if (!assignmentId || !assignmentTargetId || !assignmentAttemptId || assignmentLoadRef.current === assignmentId) return;
    assignmentLoadRef.current = assignmentId;
    setResolving(true);
    setError('');
    fetch(`/api/student/practice-assignments/${encodeURIComponent(assignmentId)}`, { headers: authHeaders() })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'Could not open this practice');
        const snapshot = data.practice?.assignment?.source?.items_snapshot || [];
        if (!snapshot.length) throw new Error('This practice has no vocabulary meanings yet');
        const exactItems = snapshot.map((item) => ({
          ...item,
          item_key: item.item_key || `${String(item.word).toLowerCase()}::${item.sense_id || 'primary'}`,
          display_label: item.display_label || item.word,
        }));
        const resolveResponse = await fetch('/api/student/self-check/resolve', {
          method: 'POST',
          headers: authHeaders(true),
          body: JSON.stringify({ words: snapshot.map((item) => item.definition_cn ? `${item.word} (${item.definition_cn})` : item.word) }),
        });
        const resolved = await resolveResponse.json().catch(() => ({}));
        const resolvedByKey = new Map((resolved.items || []).map((item) => [item.item_key || `${String(item.word).toLowerCase()}::${item.sense_id || 'primary'}`, item]));
        const completeItems = exactItems.map((item) => {
          const fallback = resolvedByKey.get(item.item_key) || {};
          return {
            ...fallback,
            ...item,
            pronunciation_sentence: item.pronunciation_sentence || fallback.pronunciation_sentence || '',
          };
        });
        setItems(completeItems);
        setOriginalItems(completeItems);
        setDistractorPool(resolved.distractor_pool || []);
        setUnresolved([]);
        setPhase(['dictation', 'meaning_sprint', 'sudden_sprint', 'pronunciation'].includes(assignmentMode) ? assignmentMode : 'mode');
      })
      .catch((loadError) => {
        assignmentLoadRef.current = '';
        setError(loadError.message || 'Could not open this practice');
      })
      .finally(() => setResolving(false));
  }, [assignmentAttemptId, assignmentId, assignmentMode, assignmentTargetId]);

  const prepare = async () => {
    setResolving(true);
    setError('');
    try {
      const response = await fetch('/api/student/self-check/resolve', { method: 'POST', headers: authHeaders(true), body: JSON.stringify({ words: parseWordText(text) }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not prepare this word set');
      if (!data.items?.length) throw new Error('None of these words were found in the dictionary');
      setItems(data.items);
      setOriginalItems(data.items);
      setDistractorPool(data.distractor_pool || []);
      setUnresolved(data.unresolved || []);
      setPhase('mode');
    } catch (prepareError) {
      setError(prepareError.message);
    } finally {
      setResolving(false);
    }
  };

  const complete = async (nextResult) => {
    const queuedModes = postLearningModesRef.current;
    const currentModeIndex = queuedModes.indexOf(nextResult.mode);
    const nextMode = currentModeIndex >= 0 ? queuedModes[currentModeIndex + 1] : '';
    if (!nextMode) {
      setResult(nextResult);
      setPhase('result');
      postLearningModesRef.current = [];
    }
    setSaving(true);
    try {
      const payload = assignmentId
        ? { ...nextResult, assignment_id: assignmentId, target_id: assignmentTargetId, attempt_id: assignmentAttemptId }
        : nextResult;
      const response = await fetch('/api/student/self-check/sessions', { method: 'POST', headers: authHeaders(true), body: JSON.stringify(payload) });
      if (!response.ok) throw new Error();
      await loadRecentHistory();
      if (nextMode) setPhase(nextMode);
    } catch (_) {
      setError('The result could not be saved, but your summary is still available here.');
      if (nextMode) setPhase(nextMode);
    } finally {
      setSaving(false);
    }
  };

  const addMistakes = async () => {
    try {
      const response = await fetch('/api/student/assign-words', { method: 'POST', headers: authHeaders(true), body: JSON.stringify({ words: result.mistakes }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Could not add missed words');
      window.alert(t('Missed words added to your learning queue.'));
    } catch (addError) {
      window.alert(addError.message);
    }
  };

  const retry = () => {
    const missed = new Set(result.mistakes.map((item) => (
      typeof item === 'string'
        ? item.toLocaleLowerCase()
        : `${item.word.toLocaleLowerCase()}::${(item.sense_ids || []).slice().sort().join('|')}`
    )));
    const retryItems = originalItems.filter((item) => {
      const grouped = groupSelfCheckItems(originalItems)
        .find((group) => group.word.toLocaleLowerCase() === item.word.toLocaleLowerCase());
      return missed.has(item.word.toLocaleLowerCase()) || (grouped && missed.has(groupIdentity(grouped)));
    });
    setItems(retryItems);
    setPhase(result.mode);
  };

  const restart = () => {
    setItems(originalItems);
    setResult(null);
    setPhase(result.mode);
  };

  const backToStart = () => {
    setItems(originalItems);
    setResult(null);
    setPhase('build');
  };

  return (
    <div className="max-w-6xl mx-auto pb-16">
      <header ref={pageTopRef} className="mb-6">
        <div className="text-xs uppercase font-semibold tracking-wide text-indigo-600">{t('Independent practice')}</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1">{t('Self Check')}</h1>
        <p className="mt-2 text-gray-600 max-w-3xl">{t('Turn the words you just studied into a focused test. Practice until recall is secure, without changing your formal progress unless you choose to.')}</p>
      </header>
      {phase === 'build' && (
        <section className="mb-6 border border-gray-200 bg-white rounded-lg shadow-sm overflow-hidden">
          <button
            type="button"
            aria-expanded={experimentsOpen}
            onClick={() => setExperimentsOpen((open) => !open)}
            className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-gray-50"
          >
            <span className="flex items-center gap-3 min-w-0">
              <span className="w-10 h-10 shrink-0 inline-flex items-center justify-center rounded-md bg-teal-100 text-teal-700"><FiBookOpen /></span>
              <span className="min-w-0">
                <span className="block font-bold text-gray-900">{t('More experiments')}</span>
                <span className="block mt-0.5 text-sm text-gray-500">{t('Explore new ways to practise with your learning history.')}</span>
              </span>
            </span>
            <span className="flex items-center gap-3 shrink-0">
              <span className="hidden sm:inline text-sm text-gray-500">{t('Available experiments', { count: EXPERIMENTS.length })}</span>
              <FiChevronDown className={`text-gray-500 transition-transform ${experimentsOpen ? 'rotate-180' : ''}`} />
            </span>
          </button>
          {experimentsOpen && (
            <div className="border-t border-gray-200 bg-gray-50 p-3 sm:p-4 space-y-2">
              {EXPERIMENTS.map((experiment) => (
                <button
                  type="button"
                  key={experiment.id}
                  onClick={() => navigate(experiment.route)}
                  className="w-full bg-white border border-gray-200 rounded-md px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-left hover:border-teal-400 hover:bg-teal-50"
                >
                  <span className="min-w-0">
                    <span className="font-semibold text-gray-900">{t(experiment.title)}</span>
                    <span className="block mt-1 text-sm leading-6 text-gray-600">{t(experiment.description)}</span>
                  </span>
                  <span className="shrink-0 flex items-center gap-3">
                    <span className="text-xs font-semibold text-teal-700 bg-teal-100 px-2 py-1 rounded">{t(experiment.duration)}</span>
                    <span className="inline-flex items-center gap-2 font-semibold text-teal-700">{t('Open')} <FiArrowRight /></span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      )}
      {error && <div className="mb-5 border border-rose-200 bg-rose-50 text-rose-800 rounded-lg px-4 py-3">{t(error)}</div>}
      {phase === 'build' && <WordSetBuilder
        sources={sources}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        text={text}
        setText={setText}
        onPrepare={prepare}
        resolving={resolving}
        history={history}
        onOpenHistory={(session) => navigate(buildIeltsSpeakingLaunchUrl({
          payload: { thread_id: session.thread_id, attempt_id: session.attempt_id || session.id },
        }))}
      />}
      {phase === 'mode' && <ModePicker items={items} unresolved={unresolved} onBack={() => setPhase('build')} onStart={setPhase} />}
      {phase === 'dictation' && <DictationRunner items={items} distractorPool={distractorPool} onFinish={complete} onExit={() => setPhase('mode')} />}
      {phase === 'meaning_sprint' && <MeaningRunner items={items} distractorPool={distractorPool} sudden={false} onFinish={complete} onExit={() => setPhase('mode')} />}
      {phase === 'sudden_sprint' && <MeaningRunner items={items} distractorPool={distractorPool} sudden onFinish={complete} onExit={() => setPhase('mode')} />}
      {phase === 'pronunciation' && <PronunciationRunner items={items} onFinish={complete} onExit={() => setPhase('mode')} />}
      {phase === 'result' && result && <Results result={result} saving={saving} onBackToStart={backToStart} onRestart={restart} onRetry={retry} onAddMistakes={addMistakes} />}
    </div>
  );
}
